import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  getPledges,
  getPledge,
  createPledge,
  updatePledge,
  createPledgeFollowUp,
  updatePledgeFollowUp,
  getOrganisationStaff,
  sumPledgePaid,
  derivePledgeStatus
} from 'lib/supabaseQueries';
import { useAuthContext } from 'context/authContext';
import useBranchPermission from './useBranchPermission';

const EMPTY_ARRAY = [];

// Attach derived money fields to a pledge row. Paid/outstanding always come
// from the linked orders, and money statuses (partially_paid/fulfilled) are
// derived — the stored status only carries workflow states.
export const withDerivedPledgeFields = pledge => {
  if (!pledge) return pledge;
  const paidTotal = sumPledgePaid(pledge.orders);
  const totalValue = pledge.total_value ? Number(pledge.total_value) : null;
  return {
    ...pledge,
    paidTotal,
    outstanding:
      totalValue !== null ? Math.max(0, totalValue - paidTotal) : null,
    effectiveStatus: derivePledgeStatus(pledge, paidTotal)
  };
};

export function usePledges() {
  const { organization, isInitialized } = useAuthContext();
  const { branchId } = useBranchPermission();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['pledges', organization?.id, branchId],
    queryFn: () => getPledges(organization.id, branchId),
    enabled: isInitialized && !!organization?.id
  });

  return {
    pledges: (data?.data || EMPTY_ARRAY).map(withDerivedPledgeFields),
    isLoading,
    error: error || data?.error,
    refetch
  };
}

export function usePledge(pledgeId) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['pledge', pledgeId],
    queryFn: () => getPledge(pledgeId),
    enabled: !!pledgeId
  });

  return {
    pledge: withDerivedPledgeFields(data?.data) || null,
    isLoading,
    error: error || data?.error,
    refetch
  };
}

export function useCreatePledge() {
  const queryClient = useQueryClient();
  const { organization } = useAuthContext();
  const { branchId } = useBranchPermission();
  const { user } = useAuthContext();

  const mutation = useMutation({
    mutationFn: async pledgeData => {
      const result = await createPledge({
        ...pledgeData,
        organisation_id: organization.id,
        branch_id: pledgeData.branch_id || branchId,
        created_by: user?.id || null
      });
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pledges'] });
      toast.success('Pledge created');
    },
    onError: error => {
      console.error('Create pledge error:', error);
      toast.error('Failed to create pledge');
    }
  });

  return {
    createPledge: mutation.mutate,
    createPledgeAsync: mutation.mutateAsync,
    isCreating: mutation.isPending
  };
}

export function useUpdatePledge() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ pledgeId, data }) => {
      const result = await updatePledge(pledgeId, data);
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pledges'] });
      queryClient.invalidateQueries({ queryKey: ['pledge'] });
      toast.success('Pledge updated');
    },
    onError: error => {
      console.error('Update pledge error:', error);
      toast.error('Failed to update pledge');
    }
  });

  return {
    updatePledge: mutation.mutate,
    updatePledgeAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending
  };
}

export function useCreatePledgeFollowUp() {
  const queryClient = useQueryClient();
  const { user } = useAuthContext();

  const mutation = useMutation({
    mutationFn: async followUpData => {
      const result = await createPledgeFollowUp({
        ...followUpData,
        created_by: user?.id || null
      });
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pledge'] });
      queryClient.invalidateQueries({ queryKey: ['pledges'] });
      toast.success('Follow-up scheduled');
    },
    onError: error => {
      console.error('Create follow-up error:', error);
      toast.error('Failed to schedule follow-up');
    }
  });

  return {
    createFollowUp: mutation.mutate,
    createFollowUpAsync: mutation.mutateAsync,
    isCreating: mutation.isPending
  };
}

export function useCompletePledgeFollowUp() {
  const queryClient = useQueryClient();
  const { user } = useAuthContext();

  const mutation = useMutation({
    mutationFn: async ({ followUpId, outcome }) => {
      const result = await updatePledgeFollowUp(followUpId, {
        status: 'completed',
        outcome,
        completed_at: new Date().toISOString(),
        completed_by: user?.id || null
      });
      if (result.error) throw new Error(result.error);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pledge'] });
      queryClient.invalidateQueries({ queryKey: ['pledges'] });
      toast.success('Follow-up recorded');
    },
    onError: error => {
      console.error('Complete follow-up error:', error);
      toast.error('Failed to record follow-up');
    }
  });

  return {
    completeFollowUp: mutation.mutate,
    completeFollowUpAsync: mutation.mutateAsync,
    isCompleting: mutation.isPending
  };
}

export function useOrganisationStaff() {
  const { organization, isInitialized } = useAuthContext();

  const { data, isLoading } = useQuery({
    queryKey: ['organisation-staff', organization?.id],
    queryFn: () => getOrganisationStaff(organization.id),
    enabled: isInitialized && !!organization?.id,
    staleTime: 300000
  });

  return {
    staff: data?.data || EMPTY_ARRAY,
    isLoading
  };
}
